import { FC } from 'react';
import {
  Box, Typography, Drawer, IconButton, Stack, Paper, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { JournalEntryPopulated } from '../../utils/invoice.types';
import { Utils } from '../../utils/common-utils';

interface JournalEntryDrawerProps {
  open: boolean;
  onClose: () => void;
  journalEntry: JournalEntryPopulated | null;
  documentTitle?: string;
}

// Combined/mirror GST ledger codes that duplicate the individual CGST/SGST/IGST entries
// BF105 = "GST Output" (mirrors CGST Payable + SGST Payable or IGST Payable for sales)
// AB107 = "GST Input" (mirrors CGST Recievable + SGST Recievable or IGST Recievable for purchases)
const COMBINED_GST_LEDGER_CODES = ['BF105', 'AB107'];

const filterDuplicateGstEntries = (entries: any[]): any[] => {
  if (!entries || entries.length === 0) return entries;

  return entries.filter((e: any) => {
    const ledgerCode = typeof e.ledger === 'object' ? (e.ledger.accountingCode || '') : '';
    return !COMBINED_GST_LEDGER_CODES.includes(ledgerCode);
  });
};

const JournalEntryDrawer: FC<JournalEntryDrawerProps> = ({ open, onClose, journalEntry, documentTitle }) => {
  if (!journalEntry) return null;

  const je = journalEntry;
  const filteredEntries = filterDuplicateGstEntries(je.entries || []);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 680 }, p: 0 }}>
        {/* Drawer Header */}
        <Box sx={{
          bgcolor: '#f5f5f5',
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e9ecf1'
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              Journal Entries
            </Typography>
            {documentTitle && (
              <Typography variant="body2" color="text.secondary">
                {documentTitle}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Drawer Content */}
        <Box sx={{ p: 2.5 }}>
          {/* JE Summary */}
          <Paper elevation={0} sx={{ p: 2, mb: 2.5, border: '1px solid #b0b8c4', borderRadius: '10px', '&.MuiPaper-root': { boxShadow: 'none' } }}>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Voucher Type</Typography>
                <Chip label={je.voucherType} size="small" color="primary" variant="outlined" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Voucher No.</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{je.voucherNumber}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Date</Typography>
                <Typography variant="body2">
                  {je.voucherDate ? new Date(je.voucherDate).toLocaleDateString('en-IN') : 'N/A'}
                </Typography>
              </Box>
              {je.paymentMode && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Payment Mode</Typography>
                  <Chip label={je.paymentMode} size="small" variant="outlined" />
                </Box>
              )}
            </Stack>
          </Paper>

          {/* Ledger Entries Table */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Ledger Entries
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #b0b8c4', borderRadius: '10px', '&.MuiPaper-root': { boxShadow: 'none' } }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>Ledger</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', minWidth: 110 }}>Debit (₹)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', minWidth: 110 }}>Credit (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEntries.map((entry: any, idx: number) => (
                  <TableRow key={idx} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {typeof entry.ledger === 'object' ? entry.ledger.name : (entry.particular || 'N/A')}
                      {entry.narration && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                          {entry.narration}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', color: entry.debit > 0 ? '#d32f2f' : 'text.secondary' }}>
                      {entry.debit > 0 ? Utils.formatNumber(entry.debit.toFixed(2)) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', color: entry.credit > 0 ? '#2e7d32' : 'text.secondary' }}>
                      {entry.credit > 0 ? Utils.formatNumber(entry.credit.toFixed(2)) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                <TableRow sx={{ bgcolor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#d32f2f' }}>
                    {Utils.formatNumber(filteredEntries.reduce((sum: number, e: any) => sum + (e.debit || 0), 0).toFixed(2))}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#2e7d32' }}>
                    {Utils.formatNumber(filteredEntries.reduce((sum: number, e: any) => sum + (e.credit || 0), 0).toFixed(2))}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Narration */}
          {je.narration && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f9fafb', borderRadius: '8px', border: '1px solid #e9ecf1' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Narration</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{je.narration}</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default JournalEntryDrawer;
